import React, { useCallback, useMemo } from 'react';
import { useReactiveStore } from '@ts-private/reactive-store';
import { stateHistoryLeasingIncomeSchemaId } from '../store';
import { Button } from '@totalsoft/rocket-ui';
import { TableCell, TableRow } from '@mui/material';
import { RoundedHeaderTableRow, SizedTableRow, ThemedHeaderTableCell } from 'components/common/table/TableStyles';
import { useTranslation } from 'react-i18next';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { format } from 'date-fns';
import FlexBox from 'components/common/FlexBox';
import { UnpaddedDialog } from 'components/common/Styles';
import MaxHeightTable from 'components/common/MaxHeightTable';
import useQuery from 'hooks/apollo/useQuery';
import { LEASING_INCOME_SCHEMA_STATE_HISTORY } from 'features/leasingIncomeSchema/queries';

const formatTransitionDate = value => {
  if (!value) return;
  const fragments = value.split('T');
  if (fragments.length !== 2) return;
  const dateObject = new Date(fragments[0]);
  const timeFragments = fragments[1].split('.');
  if (timeFragments.length !== 2) return;
  const time = timeFragments[0];
  return `${format(dateObject, 'dd/MM/yyyy')} ${time}`;
};

const StateHistoryComponent = () => {
  const { t } = useTranslation();
  const [id] = useReactiveStore(stateHistoryLeasingIncomeSchemaId);

  const { data, loading } = useQuery(LEASING_INCOME_SCHEMA_STATE_HISTORY, {
    variables: { id },
    skip: !id
  });

  const historyItems = useMemo(() => data?.leasingIncomeSchemaStateHistory || [], [data]);

  const handleClose = useCallback(() => {
    stateHistoryLeasingIncomeSchemaId.set(null);
  }, []);

  return (
    <UnpaddedDialog
      open={Boolean(id)}
      fullWidth
      maxWidth='md'
      scroll={'body'}
      disableBackdropClick
      TransitionProps={{ exit: false }}
      closeButtonProps={{ sx: { display: 'none' } }}
      fullScreen={false}
      content={
        <FlexBox direction='column' gap={2} px={2.5}>
          <MaxHeightTable maxHeight={500}>
            <MaxHeightTable.Head>
              <RoundedHeaderTableRow>
                <ThemedHeaderTableCell>{t('LeasingIncomeSchema.State')}</ThemedHeaderTableCell>
                <ThemedHeaderTableCell>{t('LeasingIncomeSchema.TransitionMoment')}</ThemedHeaderTableCell>
                <ThemedHeaderTableCell>{t('LeasingIncomeSchema.User')}</ThemedHeaderTableCell>
              </RoundedHeaderTableRow>
            </MaxHeightTable.Head>
            <MaxHeightTable.Body>
              {loading && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <WrappedFakeText lines={5} />
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                historyItems.length > 0 &&
                historyItems.map(item => (
                  <SizedTableRow key={`${item.schemaId}_${item.flowId}_${item.stateId}_${item.transitionMoment.toString()}`} height={50}>
                    <TableCell>{item.stateName}</TableCell>
                    <TableCell>{formatTransitionDate(item.transitionMoment)}</TableCell>
                    <TableCell>{item.userName}</TableCell>
                  </SizedTableRow>
                ))}
              {!loading && historyItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align='center'>
                    {t('LeasingIncomeSchema.NoTransitions')}
                  </TableCell>
                </TableRow>
              )}
            </MaxHeightTable.Body>
          </MaxHeightTable>
          <FlexBox justifyContent='end'>
            <Button onClick={handleClose}>{t('Close')}</Button>
          </FlexBox>
        </FlexBox>
      }
    />
  );
};

export default StateHistoryComponent;
